import { Alert, Button, Col, Flex, Row, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, ArcElement, Tooltip } from 'chart.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { ResponsivePie } from '@nivo/pie';

// components
import CustomCard from 'components/customCard/customCard';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NoData from 'components/noData/noData';
import MajorShareholderItem from '../components/listItem';
import TableTotal from 'components/tableTotal/tableTotal';

// styles
import styles from '../../companyInformation.module.scss';
import shareholderIcon from 'assets/icons/shareholders.svg';

// services
import { isRTL } from 'core/services/language';
import NotificationService from 'core/services/notification';
import { onExportShareholders, onGetAllShareholders, onGetMajorShareholders } from '../services/capTablesServices';

// models
import { CompanyShareholder } from '../models/shareholder';
import NumberElement from 'components/number/number';
import { downloadFile } from 'utils/downloadFile';
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';
import PageTitle from 'components/title/title';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';
import useCooperative from 'core/hooks/useCooperative';

Chart.register(ArcElement, Tooltip);

interface CapTableListPageProps {}

const CapTableListPage: FunctionComponent<CapTableListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { isCooperative } = useCooperative();

	const company = useSelector((state: RootState) => state.company);

	const [isMajorLoading, setIsMajorLoading] = useState<boolean>(true);
	const [isAllLoading, setIsAllLoading] = useState<boolean>(true);
	const [isDownloading, setIsDownloading] = useState<boolean>(false);

	const [majorShareholders, setMajorShareholders] = useState<CompanyShareholder[]>([]);
	const [allShareholders, setAllShareholders] = useState<CompanyShareholder[]>([]);

	const [remainCommonPercentage, setRemainCommonPercentage] = useState<number>(0);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps['columns'] = [
		{
			title: t('COMPANY_INFORMATION.NAME_OF_SHAREHOLDER'),
			dataIndex: 'name',
			key: 'name',
			render: (_, record) => <span>{isRTL() ? record.name : (record.name_en ?? record.name)}</span>,
		},
		{
			title: t('COMMON.NATIONAL_ID'),
			dataIndex: 'national_id',
			key: 'national_id',
		},
		{
			title: t('COMPANY_INFORMATION.TOTAL_SHARES_HOLDINGS'),
			dataIndex: 'shares',
			key: 'shares',
			render: (_, record) => <NumberElement value={record.common_shares_count} />,
		},
		{
			title: t('COMPANY_INFORMATION.PERCENTAGE_OF_HOLDINGS'),
			dataIndex: 'common_equity_percentage',
			key: 'common_equity_percentage',
			render: (_, record) => <NumberElement value={record.common_equity_percentage} decimalScale={2} percentage />,
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getMajorShareholders();
		getAllShareholders();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getMajorShareholders = async () => {
		try {
			setIsMajorLoading(true);

			const response = await onGetMajorShareholders('Common');
			setMajorShareholders(response.major_shareholders);

			const totalCommonEquity = response.major_shareholders.reduce((acc, shareholder) => {
				return acc + shareholder.common_equity_percentage;
			}, 0);

			setRemainCommonPercentage(100 - totalCommonEquity - response.treasure_shares_percentage);

			setIsMajorLoading(false);
		} catch (error) {
			setIsMajorLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const getAllShareholders = async () => {
		try {
			setIsAllLoading(true);

			const currentPage = getCurrentPage();

			const response = await onGetAllShareholders('Common', currentPage);
			setAllShareholders(response['shareholders']);
			setTotalRecords(response.pagination.total);

			setIsAllLoading(false);
		} catch (error) {
			setIsAllLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsAllLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetAllShareholders('Common', page);
			setAllShareholders(response['shareholders']);

			setCurrentPage(page);
			setIsAllLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsAllLoading(false);
		}
	};

	const exportShareholders = async () => {
		try {
			setIsDownloading(true);

			const exportResponse = await onExportShareholders('Common');

			if (exportResponse === null) {
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
				setIsDownloading(false);
				return;
			}

			await downloadFile(exportResponse!.download_url, isRTL() ? 'حقوق الملكية.xlsx' : 'Transaction log.xlsx');

			setIsDownloading(false);
		} catch (error) {
			setIsDownloading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.CAP_TABLE')} />

			{isMajorLoading ? (
				<div style={{ marginBlock: 150 }}>
					<LoadingComponent />
				</div>
			) : (
				<Row gutter={[16, 16]} style={{ marginTop: 20 }}>
					<Col span={24} md={24} lg={12}>
						<h5 className="fw-medium">{t('COMPANY_INFORMATION.MAJOR_SHAREHOLDERS')}</h5>
						<p className="mb-4">{t('COMPANY_INFORMATION.MAJOR_SHAREHOLDERS_DESC')}</p>

						<Row gutter={[16, 16]}>
							{majorShareholders.map((shareholder) => (
								<Col span={24} lg={12} key={shareholder.id}>
									<MajorShareholderItem
										common={shareholder?.common_shares_count}
										equity={shareholder?.common_equity_percentage}
										name={isRTL() ? shareholder.name : (shareholder.name_en ?? shareholder.name)}
									/>
								</Col>
							))}
						</Row>
					</Col>

					<Col span={24} md={24} lg={12}>
						<Flex vertical gap={50}>
							{!isCooperative && (
								<Alert
									message={
										<h6 className="mb-0 py-2 text-center">
											{t('COMMON.TREASURY_SHARES')}: {company?.total_number_of_treasury_shares.toLocaleString()} {isRTL() && t('COMMON.SHARES')}
										</h6>
									}
									type="info"
								/>
							)}

							<div
								style={{
									height: 350,
									direction: 'ltr',
									position: 'relative',
									marginTop: 100,
								}}>
								<img
									src={shareholderIcon}
									alt="shareholder"
									style={{
										position: 'absolute',
										top: '50%',
										left: '50%',
										transform: 'translate(-50%, -50%)',
										width: '80px',
										height: '80px',
									}}
								/>

								<ResponsivePie
									data={[
										...majorShareholders.map((shareholder) => ({
											id: isRTL() ? shareholder.first_and_last_name.ar : shareholder.first_and_last_name.en,
											label: isRTL() ? shareholder.first_and_last_name.ar : shareholder.first_and_last_name.en,
											value: shareholder.common_equity_percentage?.toFixed(),
										})),
									].concat(
										remainCommonPercentage != 0
											? [
													{
														id: t('COMMON.OTHER'),
														label: t('COMMON.OTHER'),
														value: remainCommonPercentage.toFixed(),
													},
												]
											: [],
									)}
									margin={{ top: 40, right: 120, bottom: 40, left: 120 }}
									innerRadius={0.6}
									cornerRadius={1}
									activeOuterRadiusOffset={5}
									borderWidth={1}
									borderColor={{
										from: 'color',
										modifiers: [['darker', 0.2]],
									}}
									arcLabel={(e) => `${e.value}%`}
									arcLinkLabelsSkipAngle={0}
									arcLinkLabelsTextColor="#333333"
									arcLinkLabelsThickness={2}
									arcLinkLabelsColor={{ from: 'color' }}
									arcLabelsTextColor="#fff"
									colors={['#015ddf', '#508eeb', '#357de6', '#669eeb', '#494649']}
									theme={{
										labels: {
											text: {
												fontSize: 14,
											},
										},
									}}
									tooltip={(point: any) => {
										return (
											<div style={{ backgroundColor: 'white', padding: '5px 10px', borderRadius: 'var(--radius)' }}>
												<p className="m-0 fw-medium">{point.datum.label}</p>
											</div>
										);
									}}
									animate={true}
								/>

								<div className={styles.charShadow}></div>
							</div>
						</Flex>
					</Col>
				</Row>
			)}

			<CustomCard
				title={t('COMPANY_INFORMATION.CAP_TABLE')}
				actionButton={
					<SuspendedTooltip>
						<Button type="primary" size="middle" loading={isDownloading} disabled={totalRecords === 0 || isDownloading} onClick={exportShareholders}>
							{t('COMMON.EXPORT_TO_EXCEL')}
						</Button>
					</SuspendedTooltip>
				}
				className="mt-5">
				<Table
					columns={columns}
					dataSource={allShareholders}
					scroll={{ x: 600 }}
					rowKey="id"
					pagination={{
						pageSize: 15,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
						showTotal: (total) => <TableTotal total={total} />,
					}}
					loading={isAllLoading}
					locale={{ emptyText: <NoData /> }}
				/>
			</CustomCard>
		</>
	);
};

export default CapTableListPage;
